import React, {Fragment, useEffect} from "react";
import styled from "styled-components";
import {useParams, useLocation, useNavigate, useNavigationType} from "react-router";
import {batch, useDispatch, useSelector} from "react-redux";
import {
	fetchPageScroll,
	fetchScheduleList,
	updateScheduleList
} from "redux/slices/schedule/scheduleSlice";

import {getClock, getCalendar, getDateElapse, getToday, getFormattedDate} from "utils/date";
import {firstCase, getNumber, getCommaNumber} from "utils/format";
import {getScheduleType, getScheduleTypeName, getScheduleTypeColor} from "utils/define";
import iconDrop from "assets/icons/icon_drop_black.svg";

import Observer from "components/ui/Observer";
import PopupSelect from "components/ui/popup/Select";
import SummaryInfo from "./SummaryInfo";
import ScheduleInfo from "./ScheduleInfo";



/**
 * [스케줄 목록]
 */

export const ScheduleList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const location = useLocation();
	const {scheduleType} = useParams();

	const {
		searchPartner,
		searchBranch,
		searchBranchUseYn,
		searchDate,
		searchCoach,
		searchScheduleType,
		searchCancelYn,
		scheduleList
	} = useSelector(state => state.schedule);

	const {
		data,
		pageState,
		pageSize
	} = useSelector(state => state.schedule.scheduleList);

	const isPermission = !(searchBranchUseYn == "Y" && !searchBranch);

	useEffect(() => {
		if(!isPermission) return;
		const searchInfo = (data || {}).searchInfo || {};
		const isUpdate = (
			navigationType != "POP" || (
				searchPartner != searchInfo.searchPartner ||
				searchBranch != searchInfo.searchBranch ||
				searchDate != searchInfo.searchDate ||
				searchCoach != searchInfo.searchCoach ||
				searchScheduleType != searchInfo.searchScheduleType
//				searchCancelYn != searchInfo.searchCancelYn, searchCancelYn
			)
		);
		if(isUpdate) {
			if(pageState != undefined) {
				dispatch(updateScheduleList(false));
			}
		}
	}, [searchPartner, searchBranch, searchDate, searchCoach, searchScheduleType]);

	useEffect(() => {
		if(navigationType == "POP") {
			dispatch(fetchPageScroll("restore"));
		}
	}, []);

	const eventHandler = {
		updateList : () => {
			if(!isPermission) return;
			dispatch(updateScheduleList(true));
		},
		scheduleInfo : (seqSchedule, seqPartnerClassSchedule) => {
			if(seqPartnerClassSchedule) {
				navigate("/schedule/class/" + seqPartnerClassSchedule);
			} else {
				navigate("/schedule/appointment/" + seqSchedule);
			}
		}
	};

	const render = () => {
		const scheduleList = (data || {}).scheduleList || [];
		const isEmpty = (!scheduleList.length);
		const isFilter = (searchCoach || searchScheduleType) ? true : false;
		const isCancel = (searchCancelYn == "Y");
		const today = getToday();

		const getScheduleList = () => {
			let currentDate = undefined;

			const li = scheduleList.filter(item => {
				return (isCancel) ? true : (item.scheduleState != "C");
			}).map(item => {
				const getDateInfo = () => {
					const scheduleDate = getCalendar(item.startDate);
					if(currentDate != scheduleDate) {
						currentDate = scheduleDate;

						const getScheduleTypeList = () => {
							const summaryInfo = (data || {}).summaryInfo[currentDate] || {};
							const li = [<li key={0}>전체 {getCommaNumber(summaryInfo.dailyTotalCount || 0)}</li>];
							for(let i = 1; i <= 5; i++) {
								const scheduleType = getScheduleType(i);
								const name = getScheduleTypeName(i).substr(0, 2);
								const color = getScheduleTypeColor(i);
								const count = summaryInfo["daily" + firstCase(scheduleType) + "Count"] || 0;
								const isFocus = (!searchScheduleType || searchScheduleType == scheduleType) ? "focus" : "";
								li.push(
									<li key={i} className={color + " " + isFocus}>
										{name} {getCommaNumber(count)}
									</li>
								);
							}
							return (<ul>{li}</ul>);
						};

						return (
							<li className="dateInfo" data-date={scheduleDate}>
								<div className="date">
									<div className={(scheduleDate == today) ? "today" : null}>{new Date(scheduleDate).format("sm. sd. sw")}</div>
								</div>
								<div className="summary color">
									{getScheduleTypeList()}
								</div>
							</li>
						);
					} else {
						return null;
					}
				};

				const getTimeInfo = () => {
					const startTime = getClock(item.startDate, "ap sh:MM");
					const endTime = getClock(item.endDate, "ap sh:MM");
					const scheduleTime = getDateElapse(item.startDate, item.endDate, "minute");
					return (
						<Fragment>
							<div className="startTime">{startTime}</div>
							<div className="endTime">{endTime}</div>
							<div className="scheduleTime">{scheduleTime}분</div>
						</Fragment>
					);
				};

				const sequence = item.seqSchedule || item.seqPartnerClassSchedule;

				return (
					<Fragment key={sequence}>
						{getDateInfo()}
						<li className={"scheduleInfo" + ((item.scheduleState == "C") ? " cancel" : "")}>
							<div className="left">
								{getTimeInfo()}
							</div>
							<div className="right">
								<a onClick={() => {eventHandler.scheduleInfo(item.seqSchedule, item.seqPartnerClassSchedule)}}>
									<SummaryInfo mode="list" data={item} />
								</a>
							</div>
						</li>
					</Fragment>
				);
			});

			return (<ul>{li}</ul>);
		};

		const getErrorMessage = (errorCode) => {
			let title, description;
			switch(errorCode) {
				case "NO_HEADQUARTER" :
					title = "지점별로 확인할 수 있어요.";
					description = "조회하고자 하는 지점을 선택해 주세요.";
					break;
				case "NO_SCHEDULE" :
					title = "해당 날짜에 등록된 스케줄이 없어요.";
					description = "다른 날짜를 선택해 보세요.";
					break;
				case "NO_RESULT" :
					title = "해당 조건에 등록된 스케줄이 없어요.";
					description = "검색 조건을 변경해 보세요.";
					break;
			}
			return (
				<div className="errorInfo">
					<div>
						<h4>{title}</h4>
						<h5>{description}</h5>
					</div>
				</div>
			);
		};

		const errorCode = (!isPermission) ? "NO_HEADQUARTER" : (isEmpty) ? (isFilter) ? "NO_RESULT" : "NO_SCHEDULE" : "";

		return (
			<Wrapper>
				{(errorCode && pageState != undefined) ? getErrorMessage(errorCode) : getScheduleList()}
				{(isPermission) ? <Observer isActive={pageState} isLoading={true} onVisible={eventHandler.updateList} /> : null}
			</Wrapper>
		);
	};

	return render();
};



const Wrapper = styled.div`
&									{position:relative; background-color:var(--lightBlue)}
& > ul								{padding:0 20px 20px 20px}
& > ul > li + li					{margin-top:20px}
& > ul + div.loading				{margin:-10px 0 10px}

li.empty							{padding:20px 0; text-align:center; opacity:0.66}

.dateInfo							{position:sticky; position:-webkit-sticky; top:102px; z-index:2}
.dateInfo							{margin:0 -20px; padding-bottom:8px;  background-color:var(--lightBlue); line-height:1; font-weight:600}
.dateInfo .date						{display:flex; justify-content:space-between; padding:12px 24px; background-color:var(--lightBlue); font-size:var(--14px); color:#4c5874}
.dateInfo .date	span				{padding-right:1.25em; background:url(${iconDrop}) no-repeat right center / 10px}
.dateInfo .date .today:after		{content:" · 오늘"; font-size:var(--14px); color:#4C587499}

.dateInfo .summary					{margin:0 20px; padding:10.5px 16px; background-color:#e5eeff; border-radius:6px; text-align:center; font-size:var(--13px); font-weight:500}
.dateInfo .summary .darkGray		{color:var(--darkGray)}
.dateInfo .summary ul 				{display:flex; justify-content:space-between}
.dateInfo .summary ul li			{}
.dateInfo .summary li:first-child	{padding-left:0}
.dateInfo .summary li:last-child	{padding-right:0}
.dateInfo .summary li:nth-child(n+2){opacity:0.2}
.dateInfo .summary li.focus			{opacity:1}

.scheduleInfo						{display:flex}
.scheduleInfo > .left				{padding:18px 18px 0 0; min-width:80px; text-align:right; font-size:var(--13px)}
.scheduleInfo > .left .startTime	{font-weight:bold}
.scheduleInfo > .left .scheduleTime	{display:inline-block; margin-top:5px; padding:6px 8px; background-color:white; border-radius:20px; white-space:nowrap; line-height:1; font-size:var(--11px); color:var(--darkGray)}
.scheduleInfo > .right				{width:100%; background-color:white; border-radius:8px; box-shadow:1px 2px 10px rgba(0,0,0,0.025)}

.scheduleInfo.cancel .startTime,
.scheduleInfo.cancel .endTime,
.scheduleInfo.cancel .scheduleTime	{opacity:0.2}
.scheduleInfo.cancel a > div		{position:relative}
.scheduleInfo.cancel a > div:after	{content:""; position:absolute; left:0; top:0; right:0; bottom:0; background:rgba(243,247,255,0.8)}

.dateInfo + .scheduleInfo 			{margin-top:12px}
.scheduleInfo + .dateInfo			{margin-top:20px}

.errorInfo							{position:fixed; left:0; top:240px; right:0; bottom:60px;
									 display:flex; align-items:center; justify-content:center; text-align:center}
.errorInfo h4						{margin-bottom:8px; font-weight:normal; color:#6b748b}
.errorInfo h5						{font-weight:normal; color:#949db2}
`;

export default ScheduleList;